import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks, isToday } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Loader2 } from 'lucide-react';
import {
  getPlatformIcon,
  useSocialMediaOptions,
} from '../../utils/socialMediaOptions';
import PostDetailModal from '../../components/SocialMedia/PostDetailModal';
import { resolveBadgeStyle } from '../../utils/badgeColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountCollegeTracker = () => {
  const { token, canModify } = useAuth();
  const { ACCOUNTS, ACCOUNTS_WITH_COLLEGE_EXPANSION, PLATFORM_ICONS, COLLEGES, CONTENT_TYPE_COLORS } = useSocialMediaOptions();
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedPost, setSelectedPost] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Get the week's dates
  const weekDates = useMemo(() => {
    const end = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: currentWeekStart, end });
  }, [currentWeekStart]);

  // Generate account-college rows — derived from the (now backend-driven)
  // ACCOUNTS + ACCOUNTS_WITH_COLLEGE_EXPANSION lists.
  const accountCollegeRows = useMemo(() => {
    const rows = [];
    // Section 1: All accounts with NA (no specific college)
    ACCOUNTS.forEach((account) => {
      rows.push({ account, college: 'NA', isAccountHeader: true });
    });
    // Section 2: Only expansion accounts get college sub-rows
    ACCOUNTS_WITH_COLLEGE_EXPANSION.forEach((account) => {
      COLLEGES.forEach((college) => {
        rows.push({ account, college, isAccountHeader: false });
      });
    });
    return rows;
  }, [ACCOUNTS, ACCOUNTS_WITH_COLLEGE_EXPANSION]);

  // Fetch posts and users
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        // Fetch all posts - we'll filter client-side for better UX
        const response = await axios.get(`${API}/social-media/posts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPosts(response.data);
      } catch (error) {
        toast.error('Failed to load posts');
      } finally {
        setLoading(false);
      }
    };

    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API}/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsers(response.data);
      } catch (error) {
        console.error('Failed to load users');
      }
    };

    fetchPosts();
    fetchUsers();
  }, [token]);

  // Helper function to get user name by ID
  const getUserName = (userId) => {
    if (!userId) return 'Unknown';
    const user = users.find(u => u.id === userId);
    return user ? user.full_name : 'Unknown User';
  };

  // Group posts by account, college, and date
  const getPostsForCell = (account, college, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return posts.filter(post => {
      const postAccount = post.account || '';
      const postCollege = post.college || 'NA';
      
      // Handle different date formats
      let postDateStr = null;
      if (post.scheduled_date) {
        try {
          const postDate = new Date(post.scheduled_date);
          if (!isNaN(postDate.getTime())) {
            postDateStr = format(postDate, 'yyyy-MM-dd');
          }
        } catch (e) {
          console.warn('Invalid date:', post.scheduled_date);
        }
      }
      
      const accountMatch = postAccount === account;
      const collegeMatch = college === 'NA' 
        ? (!post.college || post.college === 'NA' || post.college === '') 
        : postCollege === college;
      const dateMatch = postDateStr === dateStr;
      
      return accountMatch && collegeMatch && dateMatch;
    });
  };

  // Status color mapping
  const getStatusColor = (status) => {
    const colors = {
      'Published': 'bg-lime-500',
      'Completed': 'bg-lime-500',
      'Scheduled': 'bg-amber-500',
      'Out Delivered': 'bg-teal-500',
      'In Shoot': 'bg-blue-500',
      'In Editing': 'bg-purple-500',
      'In Corrections': 'bg-orange-500',
      'Yet to Shoot': 'bg-slate-400',
      'Pending': 'bg-slate-400',
      'Not Approved': 'bg-red-500'
    };
    return colors[status] || 'bg-slate-400';
  };

  // Status badge color mapping
  const getStatusBadgeColor = (status) => {
    const colors = {
      'Published': 'bg-lime-100 text-lime-700 border-lime-200',
      'Completed': 'bg-lime-100 text-lime-700 border-lime-200',
      'Scheduled': 'bg-amber-100 text-amber-700 border-amber-200',
      'Out Delivered': 'bg-teal-100 text-teal-700 border-teal-200',
      'In Shoot': 'bg-blue-100 text-blue-700 border-blue-200',
      'In Editing': 'bg-purple-100 text-purple-700 border-purple-200',
      'In Corrections': 'bg-orange-100 text-orange-700 border-orange-200',
      'Yet to Shoot': 'bg-slate-100 text-slate-600 border-slate-200',
      'Pending': 'bg-slate-100 text-slate-600 border-slate-200',
      'Not Approved': 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  // Platform icon helper - show all platforms
  const renderPlatformIcons = (platforms) => {
    if (!platforms || platforms.length === 0) return null;
    return (
      <div className="flex items-center gap-0.5 flex-shrink-0">
        {platforms.map((platform) => (
          <span key={platform}>{getPlatformIcon(platform, 11, PLATFORM_ICONS)}</span>
        ))}
      </div>
    );
  };

  // Navigation
  const goToPreviousWeek = () => setCurrentWeekStart(prev => subWeeks(prev, 1));
  const goToNextWeek = () => setCurrentWeekStart(prev => addWeeks(prev, 1));
  const goToCurrentWeek = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));

  // Handle post click
  const handlePostClick = (post) => {
    setSelectedPost(post);
    setIsDetailOpen(true);
  };

  // Filter out rows with no posts in the current week for cleaner view
  const rowsWithData = useMemo(() => {
    return accountCollegeRows.filter(row => {
      return weekDates.some(date => getPostsForCell(row.account, row.college, date).length > 0);
    });
  }, [accountCollegeRows, weekDates, posts]);

  // Use all rows or only rows with data based on preference
  const displayRows = accountCollegeRows; // Show all rows for complete overview

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-cyan-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tracker-page">
      {/* Header with Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Account & College Tracker</h2>
            <p className="text-sm text-slate-600" data-testid="tracker-week-label">
              Week of {format(currentWeekStart, 'MMM dd')} - {format(weekDates[weekDates.length - 1], 'MMM dd, yyyy')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToPreviousWeek} data-testid="tracker-prev-week">
              <ChevronLeft size={18} />
            </Button>
            <Button variant="outline" size="sm" onClick={goToCurrentWeek} data-testid="tracker-today">
              <CalendarIcon size={16} className="mr-1" />
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={goToNextWeek} data-testid="tracker-next-week">
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>

        {/* Status Legend */}
        <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-slate-100">
          <span className="text-xs font-semibold text-slate-500 uppercase">Status Legend:</span>
          {[
            { status: 'Published', color: 'bg-lime-500' },
            { status: 'Scheduled', color: 'bg-amber-500' },
            { status: 'Out Delivered', color: 'bg-teal-500' },
            { status: 'In Editing', color: 'bg-purple-500' },
            { status: 'Pending', color: 'bg-slate-400' }
          ].map(({ status, color }) => (
            <div key={status} className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded-full ${color}`}></div>
              <span className="text-xs text-slate-600">{status}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tracker Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse" data-testid="tracker-table">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="sticky left-0 z-20 bg-slate-50 px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase border-b border-r border-slate-200 min-w-[140px]">
                  Account
                </th>
                <th className="sticky left-[140px] z-20 bg-slate-50 px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase border-b border-r border-slate-200 min-w-[120px]">
                  College
                </th>
                {weekDates.map((date) => {
                  const isTodayColumn = isToday(date);
                  return (
                    <th 
                      key={format(date, 'yyyy-MM-dd')}
                      data-testid={`tracker-day-header-${format(date, 'yyyy-MM-dd')}`} 
                      className={`px-3 py-3 text-center text-xs font-bold uppercase border-b border-r min-w-[140px] ${
                        isTodayColumn 
                          ? 'bg-amber-100 text-amber-800 border-amber-200' 
                          : 'bg-slate-50 text-slate-700 border-slate-200'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-1">
                        {format(date, 'EEE')}
                        {isTodayColumn && <span className="text-[10px] px-1.5 py-0.5 bg-amber-500 text-white rounded-full font-semibold">TODAY</span>}
                      </div>
                      <div className={`font-normal ${isTodayColumn ? 'text-amber-600' : 'text-slate-500'}`}>
                        {format(date, 'dd-MM-yyyy')}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row, rowIndex) => {
                // Check if this is the start of account-level rows (NA section) or college expansion section
                const isAccountOnlyRow = row.college === 'NA';
                const isFirstAccountOnlyRow = rowIndex === 0;
                const isFirstCollegeRow = !isAccountOnlyRow && (rowIndex === 0 || displayRows[rowIndex - 1].college === 'NA');
                
                // Check if this is the first row of a new account group within college expansion
                const isNewAccountInCollegeSection = !isAccountOnlyRow && 
                  (isFirstCollegeRow || displayRows[rowIndex - 1].account !== row.account);
                
                // Determine row styling
                const rowBorderClass = isFirstCollegeRow 
                  ? 'border-t-4 border-cyan-300' 
                  : isNewAccountInCollegeSection 
                    ? 'border-t-2 border-slate-300' 
                    : '';
                
                // Background for account-only section vs college section
                const rowBgClass = isAccountOnlyRow ? 'bg-slate-50/50' : '';
                
                return (
                  <tr 
                    key={`${row.account}-${row.college}`} 
                    className={`hover:bg-slate-100 transition-colors ${rowBorderClass} ${rowBgClass}`}
                  >
                    <td className={`sticky left-0 z-10 px-4 py-2.5 text-sm font-semibold border-b border-r border-slate-200 ${isAccountOnlyRow ? 'bg-slate-50/50 text-slate-800' : 'bg-white text-slate-700'}`}>
                      {isAccountOnlyRow ? row.account : (isNewAccountInCollegeSection ? row.account : '')}
                    </td>
                    <td className={`sticky left-[140px] z-10 px-4 py-2.5 text-sm border-b border-r border-slate-200 ${isAccountOnlyRow ? 'bg-slate-50/50 text-slate-600 font-medium' : 'bg-white text-slate-600 pl-6'}`}>
                      {row.college}
                    </td>
                    {weekDates.map((date) => {
                      const cellPosts = getPostsForCell(row.account, row.college, date);
                      const isTodayColumn = isToday(date);
                      
                      // Determine cell background based on today highlight and row type
                      let cellBgClass = '';
                      if (isTodayColumn) {
                        cellBgClass = isAccountOnlyRow ? 'bg-amber-50/70' : 'bg-amber-50/50';
                      } else if (isAccountOnlyRow) {
                        cellBgClass = 'bg-slate-50/30';
                      }
                      
                      return (
                        <td 
                          key={format(date, 'yyyy-MM-dd')} 
                          className={`px-2 py-2 border-b align-top min-h-[60px] ${cellBgClass} ${
                            isTodayColumn ? 'border-r-2 border-l border-amber-200' : 'border-r border-slate-200'
                          }`}
                        >
                          <div className="space-y-1">
                            {cellPosts.map((post, postIndex) => (
                              <div
                                key={post.id || postIndex}
                                onClick={() => handlePostClick(post)}
                                className={`flex items-center gap-1 px-1.5 py-1 rounded-md cursor-pointer transition-all hover:shadow-md border ${getStatusBadgeColor(post.status)}`}
                                title={`${post.content_name} - ${post.content_type || 'Post'} - ${post.status}`}
                              >
                                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getStatusColor(post.status)}`}></div>
                                {renderPlatformIcons(post.platforms)}
                                {post.content_type && (() => {
                                  const ctBadge = resolveBadgeStyle(post.content_type, CONTENT_TYPE_COLORS);
                                  return (
                                    <span className={`text-[9px] font-semibold px-1 py-0.5 rounded flex-shrink-0 ${ctBadge.className}`} style={ctBadge.style}>
                                      {post.content_type}
                                    </span>
                                  );
                                })()}
                                <span className="text-[10px] font-medium truncate">
                                  {post.content_name}
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Post Detail Modal - Using standardized component */}
      <PostDetailModal
        isOpen={isDetailOpen}
        onClose={setIsDetailOpen}
        post={selectedPost}
        users={users}
        canModify={false}
        onEdit={null}
        getUserName={getUserName}
      />
    </div>
  );
};

export default AccountCollegeTracker;
